/**
 * MinhasExportacoesTable (M9, RF-M9-006 AC3) — tabela das exportações do usuário:
 * Solicitada, Escopo, Formato, Status (pill + barra de progresso quando gerando),
 * Linhas, Tamanho, Expira e botão Baixar (quando pronta).
 *
 * Exibe apenas as exportações do próprio usuário (o backend filtra por
 * solicitante — PC-04). Nenhum dado pessoal em console/analytics (RN-M11-001).
 */

import { formatarBytes, formatarData } from './format';
import { useBaixarExportacao } from '../hooks/useExportacoes';
import {
  ExportacaoEscopo,
  ExportacaoListItem,
  ExportacaoStatus,
} from '../services/exportacoes-types';

function rotuloStatus(status: ExportacaoStatus): string {
  switch (status) {
    case 'na_fila':
      return 'Na fila';
    case 'gerando':
      return 'Gerando';
    case 'pronta':
      return 'Pronta';
    case 'expirada':
      return 'Expirada';
    case 'falha':
      return 'Falhou';
    default:
      return status;
  }
}

function classeStatus(status: ExportacaoStatus): string {
  switch (status) {
    case 'pronta':
      return 'pill pill-ok';
    case 'falha':
      return 'pill pill-err';
    case 'gerando':
      return 'pill pill-dup';
    default:
      return 'pill pill-muted';
  }
}

function rotuloEscopo(escopo: ExportacaoEscopo): string {
  switch (escopo) {
    case 'tudo':
      return 'Base inteira';
    case 'filtro':
      return 'Filtro';
    case 'escola':
      return 'Escola';
    case 'escolas':
      return 'Escolas (última visita)';
    default:
      return escopo;
  }
}

function BotaoBaixar({ id }: { id: string }): JSX.Element {
  const { baixar, baixando } = useBaixarExportacao();
  return (
    <button
      type="button"
      className="btn btn-primary"
      disabled={baixando}
      onClick={() => void baixar(id)}
    >
      {baixando ? 'Gerando link…' : 'Baixar'}
    </button>
  );
}

export function MinhasExportacoesTable({
  itens,
}: {
  itens: readonly ExportacaoListItem[];
}): JSX.Element {
  if (itens.length === 0) {
    return <p className="muted">Nenhuma exportação ainda.</p>;
  }

  return (
    <table className="table">
      <thead>
        <tr>
          <th>Solicitada</th>
          <th>Escopo</th>
          <th>Formato</th>
          <th>Status</th>
          <th>Linhas</th>
          <th>Tamanho</th>
          <th>Expira</th>
          <th>Ação</th>
        </tr>
      </thead>
      <tbody>
        {itens.map((e) => (
          <tr key={e.exportacaoId}>
            <td className="mono">{formatarData(e.solicitadaEm)}</td>
            <td>{rotuloEscopo(e.escopo)}</td>
            <td>{e.formato.toUpperCase()}</td>
            <td>
              <span className={classeStatus(e.status)}>{rotuloStatus(e.status)}</span>
              {e.status === 'gerando' && (
                <div className="progress" aria-label="Gerando exportação" role="progressbar" />
              )}
            </td>
            <td>{e.linhas ?? '—'}</td>
            <td>{e.tamanhoBytes !== null ? formatarBytes(e.tamanhoBytes) : '—'}</td>
            <td className="mono">{e.expiraEm ? formatarData(e.expiraEm) : '—'}</td>
            <td>{e.status === 'pronta' ? <BotaoBaixar id={e.exportacaoId} /> : null}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
